import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import { nanoid } from '@reduxjs/toolkit'

import { useSearchParams } from '../../../../../hooks'
import { isGroup } from '../helpers/groups'
import { adjustPositions, getLastFieldRow } from '../helpers/order'
import {
  actions,
  useCreateFormFieldMutation,
  useDuplicateFieldMutation,
  useGetFormFieldsQuery,
  useRearrangeFieldsMutation,
  useRemoveFormFieldMutation,
  useUpdateFieldGroupMutation,
  useUpdateFormFieldMutation
} from '../services/formField'
import { useActionType } from './useActionType'

export function useManageField() {
  const { formId, actionTypeId } = useActionType()

  const { data, isLoading, isUninitialized } = useGetFormFieldsQuery({ formId }, { skip: !formId })

  const [searchParams, setSearchParams] = useSearchParams()

  const getFieldById = useCallback((id) => data?.entities[id] || null, [data?.entities])

  const selectedFieldId = useMemo(() => {
    const selectedField = searchParams.get('selectedField')
    return isNaN(+selectedField) ? selectedField : +selectedField
  }, [searchParams])

  const currentField = useMemo(() => getFieldById(selectedFieldId), [data?.entities[selectedFieldId], selectedFieldId])

  const [createFormField] = useCreateFormFieldMutation()
  const [updateFormField] = useUpdateFormFieldMutation()
  const [removeFormField] = useRemoveFormFieldMutation()
  const [updateFieldGroup] = useUpdateFieldGroupMutation()
  const [duplicateFormField] = useDuplicateFieldMutation()
  const [rearrangeFields] = useRearrangeFieldsMutation()

  const dispatch = useDispatch()

  const createField = useCallback(
    async ({ field }) => {
      const name = field.fieldName
      field.fieldName = name

      if (!field.isGroup) {
        field.fieldRow = getLastFieldRow(data) + 1
        field.actionTypeId = actionTypeId
        await createFormField({ formId, payload: field })
      } else {
        field.children = []
        field.id = nanoid()
        field.fieldGroup = name
        field.fieldRow = null

        dispatch(actions.addGroup(formId, field))
      }
    },
    [createFormField, dispatch, formId, data]
  )

  const duplicateField = useCallback(
    async ({ fieldId }) => {
      const field = { ...getFieldById(fieldId) }

      if (!field.isGroup) {
        field.fieldName = `${field.fieldName} (Cópia)`
        field.actionTypeId = actionTypeId
        field.fieldGroup = null

        await duplicateFormField({
          formId,
          payload: {
            fields: [field],
            lastFieldRow: getLastFieldRow(data)
          }
        })
      } else {
        const groupName = `${field.fieldName} (Cópia)`
        field.fieldName = groupName
        field.fieldGroup = groupName
        field.fieldRow = getLastFieldRow(data) + 1
        field.id = nanoid()

        const childrens = field.children.map((id) => {
          const child = { ...getFieldById(id) }
          child.fieldName = `${child.fieldName} (Cópia)`
          child.actionTypeId = actionTypeId
          child.fieldGroup = groupName

          return child
        })

        const { data: newFields } = await duplicateFormField({
          formId,
          payload: {
            fields: childrens,
            lastFieldRow: getLastFieldRow(data)
          }
        })

        field.children = newFields.map((field) => field.id)
        dispatch(actions.addGroup(formId, field))
      }
    },
    [createFormField, formId, getFieldById, data]
  )

  const persistFieldChanges = useCallback(
    async ({ fieldId }) => {
      const field = getFieldById(fieldId)
      if (!field?.unsaved) return

      if (field.isGroup) {
        const payload = field.children.map(({ id }) => ({
          id,
          fieldGroup: field.fieldName || null,
          groupType: field.groupType || null
        }))
        await updateFieldGroup({ formId, payload, fieldId })
      } else {
        await updateFormField({ formId, payload: field })
      }
    },
    [updateFormField, currentField, dispatch, formId, getFieldById]
  )

  const selectField = useCallback(
    async (fieldId) => {
      await persistFieldChanges({ fieldId: selectedFieldId, formId })
      setSearchParams({ selectedField: fieldId })
    },
    [persistFieldChanges, selectedFieldId, formId, setSearchParams]
  )

  const updateGroup = useCallback(
    ({ property, value, fieldId }) => {
      const group = getFieldById(fieldId)
      if (group.children.length) {
        const updates = group.children.map((id) => ({ id, unsaved: true }))
        dispatch(actions.updateFields(formId, [...updates, { id: fieldId, [property]: value, unsaved: true }]))
      } else {
        const updateDto = { id: fieldId, [property]: value, ...(property === 'fieldName' ? { fieldGroup: value } : {}) }
        dispatch(actions.updateField(formId, updateDto))
      }
    },
    [dispatch, formId, getFieldById]
  )

  const updateField = useCallback(
    ({ property, value, fieldId }) => {
      const field = getFieldById(fieldId)
      if (field?.isGroup) {
        updateGroup({ property, value, fieldId })
      } else {
        dispatch(actions.updateField(formId, { id: fieldId, [property]: value, unsaved: true }))
      }
    },
    [dispatch, formId, updateGroup, getFieldById]
  )

  const removeField = useCallback(
    async (fieldId) => {
      const field = getFieldById(fieldId)
      if (field?.isGroup) {
        return dispatch(actions.removeGroup(formId, fieldId))
      }

      const lastFieldRow = getLastFieldRow(data)

      const updatedFields = []

      if (field.fieldRow < lastFieldRow) {
        updatedFields.push(
          ...adjustPositions({
            fields: data,
            sourceFieldRow: field.fieldRow,
            targetFieldRow: lastFieldRow
          })
        )
      }

      if (field.fieldGroup !== null) {
        const groupId = data.ids.find((id) => isGroup(id) && getFieldById(id).fieldGroup === field.fieldGroup)
        updatedFields.push({
          id: groupId,
          children: getFieldById(groupId)?.children.filter(({ id }) => id !== fieldId)
        })
      }

      await rearrangeFields({ formId, payload: updatedFields })
      await removeFormField({ formId, fieldId })
    },
    [removeFormField, formId, getFieldById, data, selectedFieldId, setSearchParams, dispatch]
  )

  return {
    fields: data,
    getFieldById,
    currentField,
    isLoading,
    isUninitialized,
    selectedFieldId,
    selectField,
    createField,
    updateField,
    duplicateField,
    removeField,
    persistFieldChanges
  }
}
